import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, Link, Stack, Typography } from '@mui/material';

import { AccordionButton } from 'components/Button';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';

import { paths } from 'routes';

import { useGetByID } from '../../api/getById';
import { useJobAdVersions } from '../../api/getJobAdVersions';
import { DetailViewTabList } from 'components/TabList';
import { DetailsTab } from '../../components/MyAds/DetailsTab';
import { VersionsTab } from '../../components/MyAds/VersionsTab';
import { ApplicationsTab } from '../../components/MyAds/ApplicationsTab';
import { EditForm } from '../../components/EditForm';
import { StatusToggleButton } from '../../components/StatusToggleButton';
import { AssignUserForm } from '../../components/MyAds/AssignUserForm';
import { useAuth } from 'lib/auth';
import { usePageSize } from 'hook/usePageSize';
import { QnaTab, useQnaTab } from 'features/qna';
import { queryClient } from 'lib/react-query';
import { PremiumBanner } from '../../../subscriptions/components/PremiumBanner';
import { Loader } from 'components/Loader';
import { useJobAdVersionsCount } from '../../api/getJobAdVersionsCount';
import { DeleteVersionButton } from '../../components/MyAds/DeleteVersionButton';
import { getStatus } from '../../utils/getStatus';

export const DetailsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const currentTab = queryParams.get('tab') || 'details';
  const [tab, setTab] = useState(currentTab);
  const [page, setPage] = useState(1);
  const [pageSize] = usePageSize();
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(null);

  const { account, user } = useAuth();
  let { id } = useParams();
  const qnaTab = useQnaTab('job_ads', id);

  const { data: versionsCount, refetch: refetchVersionsCount } = useJobAdVersionsCount(id);

  const {
    data: jobAd,
    refetch: refetchAd,
    error,
    isError,
    isLoading: jobAdLoading,
    isFetched: jobAdFetched,
  } = useGetByID(id);

  const {
    data: versionsData,
    refetch: refetchVersions,
    isFetching: versionsFetching,
  } = useJobAdVersions(id, {
    page: page - 1,
    limit: pageSize,
    queryOptions: {
      enabled: tab === 'versions',
    },
  });

  const refetchAll = () => {
    refetchAd();
    refetchVersions();
    refetchVersionsCount();
    queryClient.invalidateQueries(['job-ad-applications', id]);
  };

  const switchTab = (_, newTab) => {
    navigate(`?tab=${newTab}`, { replace: true });
  };

  const isLoading = jobAdLoading;
  const isFetched = jobAdFetched;
  useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isLoading]);

  if (isFetched) {
    if (loading) {
      setTimeout(() => setLoading(false), 1000);
    }
  }

  useEffect(() => {
    setTab(currentTab);
  }, [currentTab]);

  if (!loading && isError && error.message === "Object doesn't exist") {
    return <div>{error?.message}</div>;
  }

  const displayStatus = jobAd ? getStatus(jobAd) : 'draft';
  const showApplicationsTab = jobAd?.application_method === 'in_app' || jobAd?.applications_count > 0;

  return (
    <ContentLayout>
      {isLoading && !loading ? (
        <></>
      ) : loading ? (
        <Loader style={{ marginTop: '40vh' }} />
      ) : !loading && isError ? (
        <PremiumBanner
          sx={{ my: 0 }}
          message={`This Job Ad is only available for Premium members. Click here for more information`}
          handleSuccess={refetchAd}
          currentLevel={user.Company.subscription_level}
        />
      ) : (
        <Stack height="100%">
          <BreadCrumbs
            items={[
              { name: '', to: paths.jobAd },
              { name: 'My Ads', to: paths.myJobAds },
              { name: jobAd?.headline ?? 'Details View' },
            ]}
          />
          <Box
            height={50}
            sx={{
              justifyContent: 'space-between',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'white',
              paddingBottom: 1.5,
              paddingTop: 1,
            }}
          >
            <Stack pl={4}>
              <Typography variant="h3">
                <Stack flex={1} flexDirection={'row'} gap={1}>
                  {jobAd?.headline || ''}
                  {(account.super_admin || user?.Company?.company_id === jobAd?.supplier_id || user?.Company?.company_id === jobAd?.buyer_id) && (
                    <Stack flex={1} sx={{ justifyContent: 'center' }}>
                      <Box
                        borderRadius="100%"
                        width={8}
                        height={8}
                        border="1px solid"
                        borderColor={
                          displayStatus === 'active'
                            ? 'success.light'
                            : displayStatus === 'draft'
                            ? 'warning.main'
                            : 'error.light'
                        }
                        bgcolor={
                          displayStatus === 'active'
                            ? 'success.light'
                            : displayStatus === 'draft'
                            ? 'warning.main'
                            : 'error.light'
                        }
                      />
                    </Stack>
                  )}
                </Stack>
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {`${jobAd?.buyer_name} (${jobAd.reference_number})`}
              </Typography>
              {jobAd?.source_ad_id && (
                <Typography variant="caption">
                  Re-posted from{' '}
                  <Link component={RouterLink} to={paths.jobAdsDetailsView(jobAd.source_ad_id)}>
                    {jobAd.source_headline || 'the original job ad'}
                  </Link>
                </Typography>
              )}
            </Stack>
            {(account.super_admin || user?.Company?.company_id === jobAd?.supplier_id || user?.Company?.company_id === jobAd?.buyer_id) && (
              <AccordionButton label="Actions" sx={{ width: 130 }}>
                <Stack gap={0.5}>
                  <AssignUserForm jobAd={jobAd} refetch={refetchAll} />
                  {(jobAd?.status === 'withdrawn' || jobAd?.status === 'draft') && (
                    <EditForm jobAd={jobAd} refetch={refetchAll} />
                  )}
                  <StatusToggleButton refetch={refetchAll} status={jobAd?.status} />
                  {jobAd?.status === 'draft' && (
                    <DeleteVersionButton
                      versionId={jobAd?.current_version_id}
                      refetch={refetchAll}
                    />
                  )}
                </Stack>
              </AccordionButton>
            )}
          </Box>
          <DetailViewTabList
            value={tab}
            onChange={switchTab}
            tabs={[
              { label: 'Details' },
              { label: 'Versions', count: versionsCount ?? 0 },
              ...(showApplicationsTab ? [{ label: 'Applications', count: jobAd?.applications_count ?? 0 }] : []),
              qnaTab,
            ]}
          />
          <Stack
            px={4}
            py={2}
            bgcolor="background.paper"
            borderTop="1px solid"
            borderColor="border.main"
            gap={1}
            height="100%"
          >
            {error && (
              <PremiumBanner
                message={`This Job Ad is only available for Premium members. Click here for more information`}
                handleSuccess={refetchAd}
                currentLevel={user.Company.subscription_level}
              />
            )}
            {!error && (
              <>
                <DetailsTab currentTab={tab} jobAd={jobAd} />
                <VersionsTab
                  currentTab={tab}
                  versionsData={versionsData}
                  isFetching={versionsFetching}
                  page={page}
                  setPage={setPage}
                />
                <ApplicationsTab currentTab={tab} jobAdID={id} />
                <QnaTab currentTab={tab} resource="job_ads" id={id} />
              </>
            )}
          </Stack>
        </Stack>
      )}
    </ContentLayout>
  );
};